import { cartsQuery } from '@/api/carts/cartQueries';
import { currentUserQuery } from '@/api/user/userQueries';
import managePractitionersIcon from '@/assets/icons/manage-practitioners.png';
import { Button } from '@/components/common/Button';
import { CardsContainer } from '@/components/dashboard/DashboardCard';
import { DashboardLinkIconCard } from '@/components/dashboard/DashboardLinkIconCard';
import { DashboardTwoColumnSection } from '@/components/dashboard/DashboardTwoColumnSection';
import { CartState } from '@/domain/Cart';
import { Role } from '@/domain/Role';
import { useHasRole } from '@/hooks/useHasRole';
import { routes } from '@/routes/routes';
import {
  Group as GroupIcon,
  QrCode as QrCodeIcon,
  Receipt as ReceiptIcon,
  ShoppingBag as ShoppingBagIcon,
  Sun as SunIcon,
  UserAvatar as UserAvatarIcon,
} from '@carbon/icons-react';
import { useQuery } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import { useMemo } from 'react';
import { DashboardHero } from '../../components/dashboard/DashboardHero';

const ManagePractitionersIcon = ({ className }: { className?: string }) => {
  return (
    <img
      src={managePractitionersIcon}
      alt=""
      className={`size-12 object-contain ${className ?? ''}`}
    />
  );
};

export const DashboardPage = () => {
  const { data: user } = useQuery(currentUserQuery);
  const { data: cartsData } = useQuery(cartsQuery);
  const { hasRole } = useHasRole();

  const doctorCreatedCartsCount = useMemo(
    () =>
      cartsData?.data.filter(
        (cart) => !!cart.created_by_doctor && cart.state === CartState.WAITING,
      ).length,
    [cartsData?.data],
  );

  return (
    <DashboardTwoColumnSection>
      <DashboardHero
        className="lg:max-w-115.5"
        Icon={SunIcon}
        iconClassName="lg:size-37.5"
        title={`Hi, ${user?.data.name}!`}
        description="Welcome! You are just a few moments away from targeting aging on the cellular level. Let's pick up where you left off."
        actionText="Have a question? Speak with an advisor"
        action={
          <Button
            asChild
            className="w-full max-w-53.5 self-center lg:self-start"
          >
            <Link to={routes.help.contactUs}>Contact Us</Link>
          </Button>
        }
      />

      {hasRole(Role.GUEST) ? (
        <p className="lg:max-w-131.5">
          Your account must be verified by shop admin. When it will be done just{' '}
          <button
            onClick={() => window.location.reload()}
            className="text-primary hover:text-secondary active:text-secondary inline-block cursor-pointer"
          >
            reload the page
          </button>
          .
        </p>
      ) : (
        <CardsContainer className="-mx-4 lg:mx-0 lg:w-full lg:max-w-131.5">
          {hasRole(Role.CUSTOMER, Role.DOCTOR) && (
            <DashboardLinkIconCard
              Icon={UserAvatarIcon}
              title="Your account"
              description="View your account details, delivery address and practitioners."
              to={routes.dashboard.account}
            />
          )}

          {hasRole(Role.CUSTOMER) && (
            <DashboardLinkIconCard
              Icon={ShoppingBagIcon}
              title="New orders"
              description="Purchase personalised supplements for yourself or your loved one."
              to={routes.dashboard.myCarts.index}
              notificationCount={doctorCreatedCartsCount}
            />
          )}

          {hasRole(Role.CUSTOMER) && (
            <DashboardLinkIconCard
              Icon={ReceiptIcon}
              title="Order history"
              description="Track your recent orders, their progress or reorder supplements."
              to={routes.dashboard.myOrders.index}
            />
          )}

          {hasRole(Role.DOCTOR) && (
            <DashboardLinkIconCard
              Icon={GroupIcon}
              title="Manage patients"
              description="Invite patients and recommend individualised supplements."
              to={routes.dashboard.users}
            />
          )}

          {hasRole(
            Role.SHOP_ADMIN,
            Role.PACKER,
            Role.DOCTOR,
            Role.SALES_MANAGER,
          ) && (
            <DashboardLinkIconCard
              Icon={ShoppingBagIcon}
              title="Orders"
              description={
                hasRole(Role.SHOP_ADMIN, Role.PACKER)
                  ? 'Track new orders and discounts.'
                  : 'Track patient subscription status and view past supplement orders.'
              }
              to={routes.dashboard.orders}
            />
          )}

          {hasRole(Role.SHOP_ADMIN) && (
            <DashboardLinkIconCard
              Icon={ManagePractitionersIcon}
              title="Practitioners"
              description="Invite new practitioners, create clinics, assign users and see statistics."
              to={routes.dashboard.practitioners}
            />
          )}

          {hasRole(Role.SHOP_ADMIN, Role.SALES_MANAGER) && (
            <DashboardLinkIconCard
              Icon={GroupIcon}
              title="Users"
              description="Invite and see all users, manage new applications, see flagged customers."
              to={routes.dashboard.users}
            />
          )}

          {hasRole(Role.SHOP_ADMIN, Role.SALES_MANAGER) && (
            <DashboardLinkIconCard
              Icon={ShoppingBagIcon}
              title="Supplements"
              description="Edit and create new supplements, manage their benefits, track stock and create protocols."
              to={routes.dashboard.supplements}
            />
          )}

          {hasRole(Role.SHOP_ADMIN) && (
            <DashboardLinkIconCard
              Icon={ReceiptIcon}
              title="Statistics"
              description="View system practitioners' performance and supplement stock insights."
              to={routes.dashboard.statistics}
            />
          )}

          {hasRole(Role.SHOP_ADMIN, Role.DOCTOR) && (
            <DashboardLinkIconCard
              Icon={QrCodeIcon}
              title="QR codes"
              description="Write your note, generate a QR code and let the patient sign up in seconds."
              to={routes.dashboard.qrCodes}
            />
          )}
        </CardsContainer>
      )}
    </DashboardTwoColumnSection>
  );
};
